import {useTranslation} from '@aboutyou/i18n';
import React from 'react';
import styled, {css} from 'styled-components';
import {mobileOnly} from '../../../constants/customMedia';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {ProductCardImageHint} from '../../molecules/ProductCard/ProductCardImageHint';
import {ProductImage} from '../../molecules/ProductImage/ProductImage';
import {ProductCardProps} from './ProductCard';

type ProductCardImgeProps = Pick<
  ProductCardProps,
  | 'product'
  | 'badges'
  | 'icon'
  | 'wishlistButton'
  | 'trackProductClick'
  | 'trackProductsOriginClick'
  | 'openInNewTab'
> & {
  overlayComponent?: React.ReactNode;
};

export const ProductCardImage = ({
  product,
  badges,
  icon,
  wishlistButton,
  trackProductClick,
  trackProductsOriginClick,
  overlayComponent,
  openInNewTab,
}: ProductCardImgeProps) => {
  const {t} = useTranslation();
  return (
    <ImageContainer
      onClick={() => {
        trackProductsOriginClick?.();
        trackProductClick?.();
      }}
      data-testid="productImageContainer"
      to={product.path}
      openLinkInNewTab={openInNewTab}
      aria-label={t('Go to the article detail page')}
    >
      {icon}
      {overlayComponent}
      {wishlistButton}
      <BadgesContainer
        data-testid="badgeAndHintContainer"
        $productDeleted={false}
      >
        <BadgeListWrapper
          data-testid="badgesList"
          $smallBottomSpace={!!product.lowStockHint}
        >
          {badges}
        </BadgeListWrapper>
        <ProductCardImageHint hint={product.lowStockHint} />
      </BadgesContainer>
      <ProductImage
        alt={product.image.description}
        columns={3}
        src={product.image.src}
        padded={product.image.isEnclosed}
        increasePadding
      />
    </ImageContainer>
  );
};

const ImageContainer = styled(UnstyledLink)`
  width: 100%;
  height: 100%;
  position: relative;
  flex: 1;
`;

const BadgesContainer = styled.div<{$productDeleted: boolean}>`
  position: absolute;
  bottom: 4px;
  display: flex;
  flex-direction: column;
  width: 100%;
  z-index: 1;
  ${({$productDeleted}) => $productDeleted && 'display: none'}
`;

const BadgeListWrapper = styled.div<{$smallBottomSpace: boolean}>`
  position: relative;
  ${({$smallBottomSpace}) =>
    $smallBottomSpace
      ? css`
          bottom: -0.5rem;
        `
      : css`
          bottom: -0.625rem;

          ${mobileOnly(css`
            bottom: -0.9rem;
          `)}
        `}

  [data-testid^="badge"] {
    &:not(:last-child) {
      margin-bottom: 0.25rem;
    }
  }
`;
